import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A round toy triceratops in the storybook style: a chubby barrel body on four
// stubby legs, a huge head crowned by a big scalloped frill, three blunt little
// horns and a cream beak. All soft masses and warm colours — a lovable plush
// dino, never a monster.
//
// Frame: faces +Z (beak and eyes forward), sits on the floor (y = 0).
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body" : the barrel belly + cream tummy patch. Breathes about the hips.
//   - "head" : skull, frill, horns, beak and eyes — one big mass swivelling
//              about the neck joint so the frill sweeps with every shake.
//   - "tail" : the stubby tail, wagging about where it leaves the body.
// The four legs are static (unnamed) so the stance stays planted.

const SKIN = "#e58e4a";        // warm terracotta-orange hide
const SKIN_DARK = "#c06f31";   // shaded orange (legs, frill studs, tail tip)
const FRILL = "#4fa8a0";       // friendly teal frill back
const FRILL_IN = "#f6efe3";    // cream frill face and belly
const BELLY = "#f6efe3";       // cream tummy and beak
const EYE_WHITE = "#fdfaf2";   // warm white (not pure white)
const EYE_DARK = "#2e2a33";    // warm-dark pupil (not pure black)
const HORN = "#efe3c8";        // soft bone-cream horns

// ── frame heights ────────────────────────────────────────────────────────────
const BODY_CY = 0.34;          // belly centre
const HIP_Y = 0.20;            // where the belly meets the legs
const NECK_Y = 0.44;           // where the head swivels
const HEAD_CY = 0.52;          // skull centre
const FRILL_CY = 0.64;         // frill centre

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, HIP_Y, 0];
const HEAD_PIVOT: [number, number, number] = [0, NECK_Y, 0.24];
const TAIL_PIVOT: [number, number, number] = [0, 0.30, -0.28];

// One stubby leg: a fat little column with a slightly wider foot disc.
function leg(x: number, z: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.065, 0.075, 0.24, 10), color: SKIN_DARK, position: [x, 0.12, z] },
    { geometry: new THREE.CylinderGeometry(0.085, 0.09, 0.05, 10), color: SKIN, position: [x, 0.025, z] },
  ];
}

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.048, 12, 10), color: EYE_WHITE, position: [x, 0.565, 0.475], name: "head" },
    { geometry: new THREE.SphereGeometry(0.026, 10, 8), color: EYE_DARK, position: [x * 0.94, 0.565, 0.512], name: "head" },
    { geometry: new THREE.SphereGeometry(0.009, 8, 6), color: EYE_WHITE, position: [x * 0.94 - 0.010, 0.578, 0.530], name: "head" },
  ];
}

// One blunt little horn: a tapered cylinder (flat tip, never a spike).
function horn(pos: [number, number, number], tilt: [number, number, number], r: number, h: number): Part {
  return { geometry: new THREE.CylinderGeometry(r * 0.4, r, h, 8), color: HORN, position: pos, rotation: tilt, name: "head" };
}

export const triceratops = defineModel({
  id: "triceratops",
  batchId: "mess",
  name: "Triceratops",
  tags: ["mess", "cute", "animated", "creature", "dinosaur", "triceratops", "toy"],
  summary: "A round toy triceratops with a big teal frill, three blunt horns and a cream beak.",
  description:
    "A lovable storybook triceratops: a chubby barrel body on four stubby legs, " +
    "a huge head crowned by a scalloped teal frill with a cream face, three " +
    "blunt bone-cream horns and a little beak. Its head and frill shake happily " +
    "side to side; the idle breathes the belly and wags the stubby tail.",
  author: "models",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      ...leg(-0.14, 0.16),
      ...leg(0.14, 0.16),
      ...leg(-0.14, -0.18),
      ...leg(0.14, -0.18),

      // ── body: a round barrel with a cream tummy ──────────────────────────
      { geometry: new THREE.SphereGeometry(0.24, 14, 12), color: SKIN,
        position: [0, BODY_CY, -0.02], scale: [1.0, 0.92, 1.25], name: "body" },
      // Cream belly patch peeking out of the chest, low and forward.
      { geometry: new THREE.SphereGeometry(0.16, 12, 10), color: BELLY,
        position: [0, 0.30, 0.14], scale: [0.92, 0.85, 0.9], name: "body" },

      // ── head: a big skull tucked under the frill ─────────────────────────
      { geometry: new THREE.SphereGeometry(0.17, 14, 12), color: SKIN,
        position: [0, HEAD_CY, 0.36], scale: [1.0, 0.95, 1.05], name: "head" },
      // Cream beak: a blunt little cone reaching forward from the snout.
      { geometry: new THREE.ConeGeometry(0.075, 0.13, 10), color: BELLY,
        position: [0, 0.475, 0.55], rotation: [Math.PI / 2, 0, 0], name: "head" },
      // Nose horn: a short blunt stub on the snout bridge.
      horn([0, 0.565, 0.51], [0.9, 0, 0], 0.038, 0.10),
      // Two brow horns pointing forward-up above the eyes.
      horn([-0.085, 0.64, 0.42], [0.75, 0, -0.18], 0.042, 0.16),
      horn([0.085, 0.64, 0.42], [0.75, 0, 0.18], 0.042, 0.16),
      ...eye(-0.095),
      ...eye(0.095),

      // ── frill: a big flattened shield tilted back over the neck ──────────
      { geometry: new THREE.SphereGeometry(0.20, 14, 12), color: FRILL,
        position: [0, FRILL_CY, 0.27], scale: [1.3, 1.15, 0.28], rotation: [-0.35, 0, 0], name: "head" },
      // Cream frill face set just forward of the teal shield (offset along the
      // tilted frill normal so it shows as a cream plate inside a teal rim).
      { geometry: new THREE.SphereGeometry(0.155, 12, 10), color: FRILL_IN,
        position: [0, FRILL_CY + 0.015, 0.315], scale: [1.3, 1.15, 0.22], rotation: [-0.35, 0, 0], name: "head" },
      // Five little scallop studs around the frill rim.
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: SKIN_DARK, position: [0, FRILL_CY + 0.23, 0.19], name: "head" },
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: SKIN_DARK, position: [-0.17, FRILL_CY + 0.17, 0.21], name: "head" },
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: SKIN_DARK, position: [0.17, FRILL_CY + 0.17, 0.21], name: "head" },
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: SKIN_DARK, position: [-0.26, FRILL_CY + 0.02, 0.26], name: "head" },
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: SKIN_DARK, position: [0.26, FRILL_CY + 0.02, 0.26], name: "head" },

      // ── tail: short, thick and wagging ───────────────────────────────────
      { geometry: new THREE.CylinderGeometry(0.045, 0.11, 0.26, 10), color: SKIN,
        position: [0, 0.245, -0.37], rotation: [-1.15, 0, 0], name: "tail" },
      { geometry: new THREE.SphereGeometry(0.05, 10, 8), color: SKIN_DARK,
        position: [0, 0.19, -0.48], name: "tail" },
    ];
  },
  animations: [
    // 1. Idle — a soft breathing bob: the belly rises, the head lifts a touch
    //    in sympathy and the stubby tail wags gently. Reads as "content".
    {
      id: "idle",
      label: "Idle breathe",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.018, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.03, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.03, 0, 0], ease: "sine" },
            { t: 3.0, value: [0.03, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.15, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.15, 0], ease: "sine" },
            { t: 3.0, value: [0, -0.15, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Head shake — the signature move: a happy side-to-side shake of the
    //    whole head-and-frill mass about the neck, with a delighted tail wag
    //    and a small counter-rock of the body.
    {
      id: "head-shake",
      label: "Happy head shake",
      duration: 2.2,
      loop: true,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.35, value: [-0.08, 0.45, 0.10], ease: "easeInOut" },
            { t: 0.75, value: [-0.08, -0.45, -0.10], ease: "easeInOut" },
            { t: 1.15, value: [-0.08, 0.45, 0.10], ease: "easeInOut" },
            { t: 1.55, value: [-0.08, -0.45, -0.10], ease: "easeInOut" },
            { t: 1.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0] },
          ],
        },
        // The body rocks gently against the shake.
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 0.35, value: [0, -0.06, -0.03], ease: "easeInOut" },
            { t: 0.75, value: [0, 0.06, 0.03], ease: "easeInOut" },
            { t: 1.15, value: [0, -0.06, -0.03], ease: "easeInOut" },
            { t: 1.55, value: [0, 0.06, 0.03], ease: "easeInOut" },
            { t: 1.9, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.2, value: [0, 0, 0] },
          ],
        },
        // A fast, delighted tail wag.
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.28, 0], ease: "sine" },
            { t: 0.55, value: [0, 0.28, 0], ease: "sine" },
            { t: 1.1, value: [0, -0.28, 0], ease: "sine" },
            { t: 1.65, value: [0, 0.28, 0], ease: "sine" },
            { t: 2.2, value: [0, -0.28, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
